import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import {
  getAgentLoanRule,
  upsertAgentLoanRule,
  listAgentLoanRules,
} from "../../api/agents";

import { SearchFilterContainer } from "../../components/SearchFilterTable";
import { InputField } from "../../components/Input";
import CheckBox from "../../components/Checkbox";
import { OutlineButton, PrimaryButton } from "../../components/Button";
import useMessageModal from "../../hooks/useMessageModal.hook";

const DEFAULT_SELECTOR = {
  companyName: "DEFAULT",
  productType: "existing",
  isDefault: true,
};

const DEFAULT_FORM = {
  companyName: "DEFAULT",
  productType: "existing",
  isDefault: true,

  minLoanAmount: "",
  maxLoanAmount: "",
  tenorMonths: "",
  requiresCheques: false,
  chequeThreshold: "",
  requires3rdPartyGuarantor: false,
  guarantorThreshold: "",
  repaymentModel: "",
};

const PRODUCT_TYPE_OPTIONS = [
  { value: "existing", label: "Existing" },
  { value: "retail", label: "Retail" },
];

function toStringOrEmpty(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  const s = String(v);
  return s === "0" ? "0" : s;
}

function toNumber(v) {
  const n = Number(String(v ?? "").replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : 0;
}

function toInt(v) {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : 0;
}

function safeLower(v) {
  return String(v ?? "").toLowerCase();
}

export default function AgentLoanRules() {
  const [setPageTitle] = useOutletContext();
  setPageTitle("Agent Loan Rules");

  const showMessageModal = useMessageModal();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selector, setSelector] = useState(DEFAULT_SELECTOR);
  const [form, setForm] = useState(DEFAULT_FORM);

  const [rulesLoading, setRulesLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [rulesSearch, setRulesSearch] = useState("");

  const canSave = useMemo(() => {
    const pt = String(form.productType || "").trim();
    const cn = String(form.companyName || "").trim();
    return !!pt && (form.isDefault || !!cn);
  }, [form.companyName, form.productType, form.isDefault]);

  const filteredRules = useMemo(() => {
    const q = safeLower(rulesSearch).trim();
    if (!q) return rules;

    return rules.filter((r) => {
      const cn = safeLower(r?.companyName);
      const pt = safeLower(r?.productType);
      const id = safeLower(r?.id);
      const def = r?.isDefault ? "yes" : "no";
      return (
        cn.includes(q) ||
        pt.includes(q) ||
        id.includes(q) ||
        def.includes(q)
      );
    });
  }, [rules, rulesSearch]);

  const toastSuccess = (message) => {
    showMessageModal({
      title: "Success",
      message,
      isError: false,
      closeButtonText: "Close",
    });
  };

  const toastError = (error, fallback) => {
    showMessageModal({
      title: "Error",
      message: error?.response?.data?.errorMessage || error?.message || fallback,
      isError: true,
      closeButtonText: "Close",
    });
  };

  // Keep form aligned with selector (for convenience)
  useEffect(() => {
    setForm((p) => ({
      ...p,
      companyName: selector.isDefault ? "DEFAULT" : selector.companyName,
      productType: selector.productType,
      isDefault: !!selector.isDefault,
    }));
  }, [selector.companyName, selector.productType, selector.isDefault]);

  async function fetchAllRules() {
    setRulesLoading(true);
    try {
      const data = await listAgentLoanRules();
      setRules(Array.isArray(data) ? data : []);
      toastSuccess("Rules loaded.");
    } catch (e) {
      console.error(e);
      toastError(e, "Failed to load rules.");
    } finally {
      setRulesLoading(false);
    }
  }

  async function fetchRule() {
    setLoading(true);
    try {
      const companyName = selector.isDefault ? "DEFAULT" : selector.companyName;
      const rule = await getAgentLoanRule({
        companyName: String(companyName || "").trim(),
        productType: String(selector.productType || "").trim(),
      });

      setForm({
        companyName: rule?.companyName ?? (selector.isDefault ? "DEFAULT" : selector.companyName),
        productType: rule?.productType ?? selector.productType,
        isDefault: !!rule?.isDefault,

        minLoanAmount: toStringOrEmpty(rule?.minLoanAmount),
        maxLoanAmount: toStringOrEmpty(rule?.maxLoanAmount),
        tenorMonths: toStringOrEmpty(rule?.tenorMonths),
        requiresCheques: !!rule?.requiresCheques,
        chequeThreshold: toStringOrEmpty(rule?.chequeThreshold),
        requires3rdPartyGuarantor: !!rule?.requires3rdPartyGuarantor,
        guarantorThreshold: toStringOrEmpty(rule?.guarantorThreshold),
        repaymentModel: String(rule?.repaymentModel ?? ""),
      });

      toastSuccess("Rule loaded.");
    } catch (e) {
      console.error(e);

      // If backend returns not found, we reset to a new rule form
      setForm((p) => ({
        ...p,
        companyName: selector.isDefault ? "DEFAULT" : selector.companyName,
        productType: selector.productType,
        isDefault: !!selector.isDefault,
      }));

      toastError(e, "Unable to load rule. If it doesn’t exist yet, you can create it by saving.");
    } finally {
      setLoading(false);
    }
  }

  async function saveRule() {
    if (!canSave) {
      toastError(null, "Company Name (or Default) and Product Type are required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        companyName: form.isDefault ? "DEFAULT" : String(form.companyName || "").trim(),
        productType: String(form.productType || "").trim(),
        isDefault: !!form.isDefault,

        minLoanAmount: toNumber(form.minLoanAmount),
        maxLoanAmount: toNumber(form.maxLoanAmount),
        tenorMonths: toInt(form.tenorMonths),
        requiresCheques: !!form.requiresCheques,
        chequeThreshold: form.requiresCheques ? toNumber(form.chequeThreshold) : 0,
        requires3rdPartyGuarantor: !!form.requires3rdPartyGuarantor,
        guarantorThreshold: form.requires3rdPartyGuarantor ? toNumber(form.guarantorThreshold) : 0,
        repaymentModel: String(form.repaymentModel || "").trim(),
      };

      await upsertAgentLoanRule(payload);
      toastSuccess("Rule saved.");

      // Refresh list (so table stays accurate)
      fetchAllRules();
    } catch (e) {
      console.error(e);
      toastError(e, "Failed to save rule.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="agents">
      {/* FIND / LIST */}
      <div className="box mb-24">
        <div className="flex between-center mb-16">
          <h3 className="fs-20">Find / List Rules</h3>
          <span className="fs-14 charcoal-80">
            Select a company name (or DEFAULT) and product type, then load or create a rule.
          </span>
        </div>

        <div className="grid gap-16">
          <div className="flex gap-16 wrap">
            <div className="flex col">
              <label className="fs-14 charcoal-60 mb-6">Default Rule</label>
              <CheckBox
                name="isDefaultSelector"
                text="Use DEFAULT rule"
                checked={!!selector.isDefault}
                setChecked={(e) =>
                  setSelector((p) => ({
                    ...p,
                    isDefault: e.target.checked,
                    companyName: e.target.checked ? "DEFAULT" : p.companyName,
                  }))
                }
              />
            </div>

            <div className="flex col" style={{ flex: 1, minWidth: 240 }}>
              <label className="fs-14 charcoal-60 mb-6">Company Name</label>
              <input
                value={selector.isDefault ? "DEFAULT" : selector.companyName}
                onChange={(e) => setSelector((p) => ({ ...p, companyName: e.target.value }))}
                placeholder="e.g Credite Capital"
                disabled={!!selector.isDefault}
              />
            </div>

            <div className="flex col" style={{ minWidth: 220 }}>
              <label className="fs-14 charcoal-60 mb-6">Product Type</label>
              <select
                value={selector.productType}
                onChange={(e) => setSelector((p) => ({ ...p, productType: e.target.value }))}
              >
                {PRODUCT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex end gap-12">
            <OutlineButton className="mini" onClick={fetchAllRules} disabled={rulesLoading}>
              {rulesLoading ? "Loading..." : "Load All Rules"}
            </OutlineButton>

            <OutlineButton className="mini" onClick={fetchRule} disabled={loading}>
              {loading ? "Loading..." : "Load Rule"}
            </OutlineButton>
          </div>

          <div className="mt-16">
            <SearchFilterContainer
              searchHint="Search rules (company, product type, id)"
              reloadTable={({ search }) => setRulesSearch(search || "")}
            >
              <table className="box fs-20 mini">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Product Type</th>
                    <th>Default</th>
                    <th>Rule ID</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {rulesLoading ? (
                    <tr>
                      <td colSpan={5} className="center-text">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredRules.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="center-text">
                        No rules loaded.
                      </td>
                    </tr>
                  ) : (
                    filteredRules.map((r) => (
                      <tr key={r?.id || `${r?.companyName}-${r?.productType}`}>
                        <td className="fs-16">{r?.companyName || "-"}</td>
                        <td className="fs-16">{r?.productType || "-"}</td>
                        <td className="fs-16">{r?.isDefault ? "Yes" : "No"}</td>
                        <td className="fs-14 charcoal-60">{r?.id || "-"}</td>
                        <td>
                          <button
                            className="btn outline mini"
                            onClick={() => {
                              setSelector({
                                companyName: r?.companyName || "",
                                productType: r?.productType || "existing",
                                isDefault: !!r?.isDefault,
                              });

                              setForm({
                                companyName: r?.companyName || "",
                                productType: r?.productType || "existing",
                                isDefault: !!r?.isDefault,
                                minLoanAmount: toStringOrEmpty(r?.minLoanAmount),
                                maxLoanAmount: toStringOrEmpty(r?.maxLoanAmount),
                                tenorMonths: toStringOrEmpty(r?.tenorMonths),
                                requiresCheques: !!r?.requiresCheques,
                                chequeThreshold: toStringOrEmpty(r?.chequeThreshold),
                                requires3rdPartyGuarantor: !!r?.requires3rdPartyGuarantor,
                                guarantorThreshold: toStringOrEmpty(r?.guarantorThreshold),
                                repaymentModel: String(r?.repaymentModel ?? ""),
                              });
                            }}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SearchFilterContainer>
          </div>
        </div>
      </div>

      {/* EDIT / UPSERT */}
      <div className="box">
        <div className="flex between-center mb-16">
          <h3 className="fs-20">Rule Details</h3>
          <span className="fs-14 charcoal-80">Update limits and requirements, then save.</span>
        </div>

        <div className="grid gap-16">
          <div className="flex col">
            <CheckBox
              name="isDefault"
              text="This is the DEFAULT rule"
              checked={!!form.isDefault}
              setChecked={(e) =>
                setForm((p) => ({
                  ...p,
                  isDefault: e.target.checked,
                  companyName: e.target.checked ? "DEFAULT" : p.companyName,
                }))
              }
            />
          </div>

          <div className="grid gap-16" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            <InputField
              label="Company Name"
              value={form.isDefault ? "DEFAULT" : form.companyName}
              onChange={(e) => setForm((p) => ({ ...p, companyName: e.target.value }))}
              placeholder="e.g Credite Capital"
              disabled={!!form.isDefault}
            />

            <div className="flex col">
              <label className="fs-14 charcoal-60 mb-6">Product Type</label>
              <select
                value={form.productType}
                onChange={(e) => setForm((p) => ({ ...p, productType: e.target.value }))}
              >
                {PRODUCT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <InputField
              label="Min Loan Amount"
              value={form.minLoanAmount}
              onChange={(e) => setForm((p) => ({ ...p, minLoanAmount: e.target.value }))}
              placeholder="e.g 10000"
            />

            <InputField
              label="Max Loan Amount"
              value={form.maxLoanAmount}
              onChange={(e) => setForm((p) => ({ ...p, maxLoanAmount: e.target.value }))}
              placeholder="e.g 200000"
            />

            <InputField
              label="Tenor (Months)"
              value={form.tenorMonths}
              onChange={(e) => setForm((p) => ({ ...p, tenorMonths: e.target.value }))}
              placeholder="e.g 1"
            />

            <InputField
              label="Repayment Model"
              value={form.repaymentModel}
              onChange={(e) => setForm((p) => ({ ...p, repaymentModel: e.target.value }))}
              placeholder="e.g salary_deduction"
            />
          </div>

          <div className="box mini">
            <div className="flex gap-24 wrap">
              <CheckBox
                name="requiresCheques"
                text="Requires Cheques"
                checked={!!form.requiresCheques}
                setChecked={(e) =>
                  setForm((p) => ({
                    ...p,
                    requiresCheques: e.target.checked,
                    chequeThreshold: e.target.checked ? p.chequeThreshold : "",
                  }))
                }
              />

              <div style={{ minWidth: 240 }}>
                <InputField
                  label="Cheque Threshold"
                  value={form.chequeThreshold}
                  onChange={(e) => setForm((p) => ({ ...p, chequeThreshold: e.target.value }))}
                  placeholder="e.g 50000"
                  disabled={!form.requiresCheques}
                />
              </div>
            </div>

            <div className="flex gap-24 wrap mt-16">
              <CheckBox
                name="requires3rdPartyGuarantor"
                text="Requires 3rd Party Guarantor"
                checked={!!form.requires3rdPartyGuarantor}
                setChecked={(e) =>
                  setForm((p) => ({
                    ...p,
                    requires3rdPartyGuarantor: e.target.checked,
                    guarantorThreshold: e.target.checked ? p.guarantorThreshold : "",
                  }))
                }
              />

              <div style={{ minWidth: 240 }}>
                <InputField
                  label="Guarantor Threshold"
                  value={form.guarantorThreshold}
                  onChange={(e) => setForm((p) => ({ ...p, guarantorThreshold: e.target.value }))}
                  placeholder="e.g 100000"
                  disabled={!form.requires3rdPartyGuarantor}
                />
              </div>
            </div>
          </div>

          <div className="flex end gap-12">
            <OutlineButton
              className="mini"
              onClick={() => {
                setSelector(DEFAULT_SELECTOR);
                setForm(DEFAULT_FORM);
              }}
              disabled={saving || loading}
            >
              Reset
            </OutlineButton>

            <PrimaryButton className="mini" onClick={saveRule} disabled={saving || !canSave}>
              {saving ? "Saving..." : "Save Rule"}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}
